import type { ChangeLogEntryDto, ChangeLogFieldChangeDto } from '../../../../api/created/changeLog.ts';
import type { LocaleDto, UserDto } from '../../../../api/generated/model';
import { PatientDtoGender, PatientDtoResidencyPermit } from '../../../../api/generated/model';
import { PersonLivingStatus } from '../../../../api/generated/person-controller/person-controller.ts';
import type {
  ChangeHistoryEntryData,
  HistoryAction,
  HistoryFieldChange,
} from '../../../../components/history/historyTypes.ts';
import { getGenderName } from '../../../../utils/gender.ts';
import { resolveChangedByName } from '../../../../utils/user.ts';
import { getLivingStatusName } from '../../../../utils/livingStatus.ts';
import { getResidencyPermitName } from '../../../../utils/residencyPermit.ts';
import { getSalutationName } from '../../../../utils/salutation.ts';

export type PersonFieldChange = HistoryFieldChange;

export type PersonHistoryEntry = ChangeHistoryEntryData & {
  id: string;
  action: HistoryAction;
  actionLabel: string;
  changedByName: string;
};

export type PersonHistoryMappingContext = {
  users: UserDto[];
  countries: LocaleDto[];
  languages: LocaleDto[];
};

const PERSON_FIELD_LABELS: Record<string, string> = {
  first_name: 'Vorname',
  last_name: 'Nachname',
  birth_date: 'Geburtsdatum',
  gender: 'Geschlecht',
  phone: 'Telefon Nr. Mobil',
  email: 'E-Mail-Address',
  nationality_country_code: 'Nationalität',
  origin_country_code: 'Herkunftsland',
  arrival: 'Einreisedatum',
  residency_permit: 'FREPO-Status',
  asylum_number: 'ZEMIS-Nummer',
  additional_information: 'Zusatz',
  street: 'strasse',
  postal_code: 'PLZ',
  city: 'Ort',
  state: 'Kanton',
  zemis_number: 'ZEMIS-Nummer',
  salutation: 'Anrede',
  living_status: 'Lebenssituation',
  note: 'Bemerkungen',
  entry_date: 'Eintrittsdatum',
  mobile_phone_number: 'Telefon Nr. Mobil',
  languages: 'Sprachen',
  private_phone_number: 'Telefon Nr. Privat',
  work_phone_number: 'Telefon Nr. Geschäft',
};

const ACTION_LABELS: Record<HistoryAction, string> = {
  created: 'wurden erstellt von',
  updated: 'wurden geändert von',
  deleted: 'wurden gelöscht von',
};

function mapOperationToAction(operation: string): HistoryAction {
  const normalizedOperation = operation.trim().toUpperCase();

  if (normalizedOperation.includes('CREATE') || normalizedOperation.includes('INSERT')) {
    return 'created';
  }

  if (normalizedOperation.includes('DELETE') || normalizedOperation.includes('REMOVE')) {
    return 'deleted';
  }

  return 'updated';
}

function getPersonFieldLabel(fieldName: string) {
  return PERSON_FIELD_LABELS[fieldName] ?? fieldName;
}

function isPatientDtoGender(value: string): value is PatientDtoGender {
  return Object.values(PatientDtoGender).includes(value as PatientDtoGender);
}

function isPatientDtoResidencyPermit(value: string): value is PatientDtoResidencyPermit {
  return Object.values(PatientDtoResidencyPermit).includes(value as PatientDtoResidencyPermit);
}

function isPersonLivingStatus(value: string): value is PersonLivingStatus {
  return Object.values(PersonLivingStatus).includes(value as PersonLivingStatus);
}

function formatCountryCode(value: string, countries: LocaleDto[]) {
  const country = countries.find((candidate) => candidate.isoCode.toLowerCase() === value.toLowerCase());
  return country?.name ?? value;
}

function getLanguageCodeFromParsedItem(item: unknown) {
  if (typeof item === 'string') {
    return item;
  }

  if (typeof item === 'object' && item !== null && 'isoCode' in item) {
    const isoCode = (item as { isoCode: unknown }).isoCode;
    if (typeof isoCode === 'string') {
      return isoCode;
    }
    if (typeof isoCode === 'number' || typeof isoCode === 'boolean') {
      return String(isoCode);
    }
  }

  return '';
}

function parseLanguageCodes(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed.map(getLanguageCodeFromParsedItem).filter(Boolean);
    }
  } catch {
    // Not JSON — fall through to comma-separated parsing.
  }

  return trimmed
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean);
}

function formatLanguages(value: string, languages: LocaleDto[]) {
  const codes = parseLanguageCodes(value);
  if (codes.length === 0) {
    return value;
  }

  return codes
    .map((code) => {
      const language = languages.find((candidate) => candidate.isoCode.toLowerCase() === code.toLowerCase());
      return language?.name ?? code;
    })
    .join(', ');
}

export function formatPersonFieldValue(
  fieldName: string,
  value: string | null | undefined,
  context: PersonHistoryMappingContext,
) {
  if (value == null || !value.trim()) {
    return value ?? '';
  }

  switch (fieldName) {
    case 'salutation': {
      const name = getSalutationName(value);
      return name === '-' ? value : name;
    }
    case 'gender':
      return isPatientDtoGender(value) ? getGenderName(value) : value;
    case 'residency_permit':
      return isPatientDtoResidencyPermit(value) ? getResidencyPermitName(value) : value;
    case 'living_status':
      return isPersonLivingStatus(value) ? getLivingStatusName(value) : value;
    case 'nationality_country_code':
    case 'origin_country_code':
      return formatCountryCode(value, context.countries);
    case 'languages':
      return formatLanguages(value, context.languages);
    default:
      return value;
  }
}

function mapFieldChange(change: ChangeLogFieldChangeDto, context: PersonHistoryMappingContext): HistoryFieldChange {
  return {
    fieldLabel: getPersonFieldLabel(change.fieldName),
    oldValue: formatPersonFieldValue(change.fieldName, change.oldValue, context),
    newValue: formatPersonFieldValue(change.fieldName, change.newValue, context),
  };
}

export function mapChangeLogEntryToPersonHistoryEntry(
  entry: ChangeLogEntryDto,
  context: PersonHistoryMappingContext,
): PersonHistoryEntry {
  const action = mapOperationToAction(entry.operation);

  return {
    id: entry.changeSetId,
    date: entry.changedAt,
    action,
    actionLabel: ACTION_LABELS[action],
    changedByName: resolveChangedByName(entry.changedBy, context.users),
    changes: entry.changes.map((change) => mapFieldChange(change, context)),
  };
}

export function mapChangeLogEntriesToPersonHistory(
  entries: ChangeLogEntryDto[],
  context: PersonHistoryMappingContext,
): PersonHistoryEntry[] {
  return entries.map((entry) => mapChangeLogEntryToPersonHistoryEntry(entry, context));
}
